import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../../../../css/common/chat/ChatFollowupInbox.css';
import EventFlyerMedia from '../../EventFlyerMedia';
import { useToast } from '../../ToastSystem';
import AiFluidMark from '../shared/AiFluidMark';
import {
  completeAdminChatFollowupItem,
  promoteAdminChatFollowupItem,
  fetchAdminChatFollowupInbox,
  extractChatFollowupOpenCount,
  resolveChatFollowupAgeLabel,
  resolveChatFollowupAnchorExcerpt,
  resolveChatFollowupAnchorSenderLabel,
  resolveChatFollowupAssigneeAvatarUrl,
  resolveChatFollowupAssigneeBasisLabel,
  resolveChatFollowupAssigneeName,
  resolveChatFollowupChannel,
  resolveChatFollowupEventFlyer,
  resolveChatFollowupItemId,
  resolveChatFollowupKind,
  resolveChatFollowupKindLabel,
  resolveChatFollowupMetaText,
  resolveChatFollowupStage,
  resolveChatFollowupTargetAvatarUrl,
  resolveChatFollowupTargetInitial,
  resolveChatFollowupTargetName,
  resolveChatFollowupWaitingOn,
} from './chatFollowupApi';

const joinClassNames = (...tokens) => tokens.filter(Boolean).join(' ');
const OPENING_AI_ANIMATION_MS = 2600;
const OPENING_AI_ANIMATION_WITH_LIST_MS = Math.max(0, OPENING_AI_ANIMATION_MS - 1000);
const AI_ANIMATION_EXIT_MS = 360;
const TASK_COMPLETE_EXIT_MS = 2600;

const resolveOpeningAnimationMs = (summary) =>
  extractChatFollowupOpenCount(summary) > 0
    ? OPENING_AI_ANIMATION_WITH_LIST_MS
    : OPENING_AI_ANIMATION_MS;

// 緊急度は色の1軸だけ（左カラーバー廃止・控えめテキスト）。escalated=優先, followup=要対応, それ以外=様子見。
const resolveUrgency = (stage) => {
  switch (String(stage || '').toLowerCase()) {
    case 'escalated':
      return { label: '優先対応', tone: 'danger', icon: 'fa-solid fa-bolt' };
    case 'followup':
      return { label: '要対応', tone: 'warning', icon: 'fa-solid fa-triangle-exclamation' };
    default:
      return { label: '様子見', tone: 'muted', icon: '' };
  }
};

const resolveKindIconClass = (kind) => {
  switch (String(kind || '').toLowerCase()) {
    case 'admin_commitment':
      return 'fa-regular fa-clock';
    case 'unanswered_message':
      return 'fa-regular fa-comment-dots';
    case 'unresolved_issue':
      return 'fa-regular fa-circle-question';
    case 'dispute':
      return 'fa-solid fa-triangle-exclamation';
    default:
      return 'fa-regular fa-comment';
  }
};

const Avatar = ({ imageUrl = '', fallback = '', className = '' }) => {
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    setHasError(false);
  }, [imageUrl]);
  return (
    <span className={joinClassNames('chat-followup-inbox__avatar', className)}>
      {imageUrl && !hasError ? (
        <img src={imageUrl} alt="" onError={() => setHasError(true)} />
      ) : (
        <span>{fallback || '?'}</span>
      )}
    </span>
  );
};

// 相手を視認できる視覚の軸: イベント=フライヤー / アーティスト=顔写真 / グループ=アイコン。
const TargetMedia = ({ item }) => {
  const channel = resolveChatFollowupChannel(item);
  const targetName = resolveChatFollowupTargetName(item);
  if (channel === 'event') {
    const flyer = resolveChatFollowupEventFlyer(item) || {};
    return (
      <EventFlyerMedia
        className="chat-followup-inbox__flyer"
        imageUrl={flyer.flyerFrontUrl || ''}
        imageAlt={targetName}
        placeholderDesignKey={flyer.placeholderDesignKey || ''}
        placeholderColorKey={flyer.placeholderColorKey || ''}
        hasImage={Boolean(flyer.flyerFrontUrl)}
        sizeVariant="small"
        aspectRatio="1 / 1.414"
        borderRadius={8}
      />
    );
  }
  if (channel === 'group') {
    return (
      <span className="chat-followup-inbox__media chat-followup-inbox__media--group" aria-hidden="true">
        <i className="fa-solid fa-user-group"></i>
      </span>
    );
  }
  const avatarUrl = resolveChatFollowupTargetAvatarUrl(item);
  const initial = resolveChatFollowupTargetInitial(item) || targetName.slice(0, 1);
  return <Avatar imageUrl={avatarUrl} fallback={initial} className="is-target" />;
};

const ChatFollowupInbox = ({
  className = '',
  initialSummary,
  onSummaryChange,
  onOpenTask,
  onBack,
  showBackButton = false,
  refreshKey = 0,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingAnimationReady, setOpeningAnimationReady] = useState(false);
  const [openingAnimationMs, setOpeningAnimationMs] = useState(() =>
    resolveOpeningAnimationMs(initialSummary)
  );
  const [aiAnimationStage, setAiAnimationStage] = useState('visible');
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [completingIds, setCompletingIds] = useState(() => new Set());
  const [promotingIds, setPromotingIds] = useState(() => new Set());
  const toast = useToast();
  const openingAnimationStartedAtRef = useRef(Date.now());
  const initialSummaryRef = useRef(initialSummary);
  const itemsRef = useRef([]);
  const hasLoadedOnceRef = useRef(false);
  const activeMutationIdsRef = useRef(new Set());
  const hiddenItemIdsRef = useRef(new Set());

  const addBusyId = useCallback((itemId) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  }, []);

  const removeBusyId = useCallback((itemId) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  const addCompletingId = useCallback((itemId) => {
    setCompletingIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  }, []);

  const removeCompletingId = useCallback((itemId) => {
    setCompletingIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  const addPromotingId = useCallback((itemId) => {
    setPromotingIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  }, []);

  const removePromotingId = useCallback((itemId) => {
    setPromotingIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  const removeItemById = useCallback((itemId) => {
    setItems((prev) => {
      const next = prev.filter((candidate) => resolveChatFollowupItemId(candidate) !== itemId);
      itemsRef.current = next;
      return next;
    });
  }, []);

  const restoreItem = useCallback((item) => {
    const itemId = resolveChatFollowupItemId(item);
    if (!itemId) return;
    setItems((prev) => {
      if (prev.some((candidate) => resolveChatFollowupItemId(candidate) === itemId)) return prev;
      const next = [item, ...prev];
      itemsRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    initialSummaryRef.current = initialSummary;
    if (!hasLoadedOnceRef.current) {
      setOpeningAnimationMs(resolveOpeningAnimationMs(initialSummary));
    }
  }, [initialSummary]);

  const loadInbox = useCallback(
    async ({ signal, refresh = false, restartAnimation = false } = {}) => {
      const hasCachedList = itemsRef.current.length > 0;
      if (restartAnimation) {
        openingAnimationStartedAtRef.current = Date.now();
        setOpeningAnimationMs(
          hasCachedList
            ? OPENING_AI_ANIMATION_WITH_LIST_MS
            : resolveOpeningAnimationMs(initialSummaryRef.current)
        );
        setOpeningAnimationReady(false);
        setAiAnimationStage('visible');
      }
      setLoading(!(restartAnimation && hasLoadedOnceRef.current && hasCachedList));
      try {
        const payload = await fetchAdminChatFollowupInbox({ limit: 12, signal, refresh });
        const receivedItems = Array.isArray(payload.items) ? payload.items : [];
        const nextItems = receivedItems.filter(
          (candidate) => !hiddenItemIdsRef.current.has(resolveChatFollowupItemId(candidate)),
        );
        itemsRef.current = nextItems;
        hasLoadedOnceRef.current = true;
        setItems(nextItems);
        setOpeningAnimationMs(nextItems.length > 0 ? OPENING_AI_ANIMATION_WITH_LIST_MS : OPENING_AI_ANIMATION_MS);
        onSummaryChange?.(payload.summary);
      } finally {
        setLoading(false);
      }
    },
    [onSummaryChange],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadInbox({ signal: controller.signal }).catch(() => {});
    return () => controller.abort();
  }, [loadInbox]);

  useEffect(() => {
    if (!refreshKey) return undefined;
    const controller = new AbortController();
    loadInbox({ signal: controller.signal, refresh: true, restartAnimation: true }).catch(() => {});
    return () => controller.abort();
  }, [loadInbox, refreshKey]);

  useEffect(() => {
    if (aiAnimationStage !== 'visible') return undefined;
    setOpeningAnimationReady(false);
    const elapsed = Date.now() - openingAnimationStartedAtRef.current;
    const delay = Math.max(0, openingAnimationMs - elapsed);
    const timer = window.setTimeout(() => {
      setOpeningAnimationReady(true);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [aiAnimationStage, openingAnimationMs, refreshKey]);

  useEffect(() => {
    if (loading || !openingAnimationReady || aiAnimationStage !== 'visible') return;
    setAiAnimationStage('exiting');
  }, [aiAnimationStage, loading, openingAnimationReady]);

  useEffect(() => {
    if (aiAnimationStage !== 'exiting') return undefined;
    const timer = window.setTimeout(() => {
      setAiAnimationStage('done');
    }, AI_ANIMATION_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [aiAnimationStage]);

  const handleComplete = useCallback(
    async (item) => {
      const itemId = resolveChatFollowupItemId(item);
      if (!itemId || activeMutationIdsRef.current.has(itemId)) return;
      activeMutationIdsRef.current.add(itemId);
      addBusyId(itemId);
      addCompletingId(itemId);
      const exitDelay = new Promise((resolve) => {
        window.setTimeout(resolve, TASK_COMPLETE_EXIT_MS);
      });
      const completionResultPromise = completeAdminChatFollowupItem({ itemId }).then(
        () => ({ ok: true }),
        (error) => ({ ok: false, error }),
      );
      let removedOptimistically = false;
      try {
        const firstResult = await Promise.race([
          completionResultPromise,
          exitDelay.then(() => ({ exitReady: true })),
        ]);
        if (firstResult?.ok === false) {
          throw firstResult.error;
        }
        if (!firstResult?.exitReady) {
          await exitDelay;
        }
        hiddenItemIdsRef.current.add(itemId);
        removeItemById(itemId);
        removedOptimistically = true;
        removeCompletingId(itemId);
        const finalResult = firstResult?.exitReady ? await completionResultPromise : firstResult;
        if (finalResult?.ok === false) {
          throw finalResult.error;
        }
        await loadInbox({ refresh: true });
      } catch (error) {
        hiddenItemIdsRef.current.delete(itemId);
        removeCompletingId(itemId);
        if (removedOptimistically) {
          restoreItem(item);
        }
        toast.error(error?.message || '対応済み更新に失敗しました。時間をおいて再度お試しください。');
      } finally {
        activeMutationIdsRef.current.delete(itemId);
        removeBusyId(itemId);
      }
    },
    [addBusyId, addCompletingId, loadInbox, removeBusyId, removeCompletingId, removeItemById, restoreItem, toast],
  );

  const handlePromote = useCallback(
    async (item) => {
      const itemId = resolveChatFollowupItemId(item);
      if (!itemId || activeMutationIdsRef.current.has(itemId)) return;
      activeMutationIdsRef.current.add(itemId);
      addBusyId(itemId);
      addPromotingId(itemId);
      const exitDelay = new Promise((resolve) => {
        window.setTimeout(resolve, TASK_COMPLETE_EXIT_MS);
      });
      const promoteResultPromise = promoteAdminChatFollowupItem({ itemId }).then(
        () => ({ ok: true }),
        (error) => ({ ok: false, error }),
      );
      let removedOptimistically = false;
      try {
        const firstResult = await Promise.race([
          promoteResultPromise,
          exitDelay.then(() => ({ exitReady: true })),
        ]);
        if (firstResult?.ok === false) {
          throw firstResult.error;
        }
        if (!firstResult?.exitReady) {
          await exitDelay;
        }
        hiddenItemIdsRef.current.add(itemId);
        removeItemById(itemId);
        removedOptimistically = true;
        removePromotingId(itemId);
        const finalResult = firstResult?.exitReady ? await promoteResultPromise : firstResult;
        if (finalResult?.ok === false) {
          throw finalResult.error;
        }
        await loadInbox({ refresh: true });
      } catch (error) {
        hiddenItemIdsRef.current.delete(itemId);
        removePromotingId(itemId);
        if (removedOptimistically) {
          restoreItem(item);
        }
        toast.error(error?.message || 'クイックタスク化に失敗しました。時間をおいて再度お試しください。');
      } finally {
        activeMutationIdsRef.current.delete(itemId);
        removeBusyId(itemId);
      }
    },
    [addBusyId, addPromotingId, loadInbox, removeBusyId, removeItemById, removePromotingId, restoreItem, toast],
  );

  return (
    <div className={joinClassNames('chat-followup-inbox', className)}>
      {showBackButton ? (
        <button
          type="button"
          className="chat-followup-inbox__backButton"
          onClick={onBack}
          aria-label="一覧に戻る"
          title="一覧に戻る"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
        </button>
      ) : null}

      {aiAnimationStage !== 'done' ? (
        <div
          className={joinClassNames(
            'chat-followup-inbox__centerStage',
            'is-loading',
            aiAnimationStage === 'exiting' && 'is-exiting',
          )}
          aria-label="AIが対応漏れを確認中"
        >
          <AiFluidMark variant="loading" className="chat-followup-inbox__loadingMark" />
        </div>
      ) : items.length === 0 ? (
        <div className="chat-followup-inbox__centerStage is-empty chat-followup-inbox__result">
          <div className="chat-followup-inbox__emptyTitle">現在対応漏れはありません。</div>
          <p className="chat-followup-inbox__emptyText">AIは間違えることがあります。</p>
        </div>
      ) : (
        <div className="chat-followup-inbox__list chat-followup-inbox__result">
          {items.map((item) => {
            const itemId =
              resolveChatFollowupItemId(item) ||
              `${resolveChatFollowupChannel(item)}-${resolveChatFollowupTargetName(item)}`;
            const stage = resolveChatFollowupStage(item);
            const urgency = resolveUrgency(stage);
            const targetName = resolveChatFollowupTargetName(item);
            const kind = resolveChatFollowupKind(item);
            const kindLabel = resolveChatFollowupKindLabel(kind);
            const ageLabel = resolveChatFollowupAgeLabel(item);
            const anchorExcerpt = resolveChatFollowupAnchorExcerpt(item) || resolveChatFollowupMetaText(item);
            const anchorSenderLabel = resolveChatFollowupAnchorSenderLabel(item);
            const waitingOnArtist = resolveChatFollowupWaitingOn(item) === 'artist';
            const assigneeName = resolveChatFollowupAssigneeName(item);
            const assigneeAvatarUrl = resolveChatFollowupAssigneeAvatarUrl(item);
            const assigneeBasisLabel = resolveChatFollowupAssigneeBasisLabel(item);
            const isBusy = busyIds.has(itemId);
            const isCompleting = completingIds.has(itemId);
            const isPromoting = promotingIds.has(itemId);
            const isExiting = isCompleting || isPromoting;

            return (
              <div
                key={itemId}
                role="button"
                tabIndex={0}
                aria-disabled={isBusy || isExiting}
                className={joinClassNames(
                  'chat-followup-inbox__task',
                  `is-stage-${stage}`,
                  isCompleting && 'is-completing',
                  isPromoting && 'is-promoting',
                )}
                onClick={() => {
                  if (isBusy || isExiting) return;
                  onOpenTask?.(item);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (isBusy || isExiting) return;
                    onOpenTask?.(item);
                  }
                }}
              >
                <div className="chat-followup-inbox__taskSolid" aria-hidden="true"></div>
                <div className="chat-followup-inbox__taskFluid" aria-hidden="true">
                  <AiFluidMark variant="loading" className="chat-followup-inbox__completeMark" />
                </div>
                <div className="chat-followup-inbox__taskContent">
                  <div className="chat-followup-inbox__media-slot">
                    <TargetMedia item={item} />
                  </div>

                  <div className="chat-followup-inbox__body">
                    <div className="chat-followup-inbox__headline">
                      <div className="chat-followup-inbox__headlineLeft">
                        <span className="chat-followup-inbox__target">{targetName}</span>
                        {kindLabel ? (
                          <span className={joinClassNames('chat-followup-inbox__kind', `is-kind-${kind}`)}>
                            <i className={resolveKindIconClass(kind)} aria-hidden="true"></i>
                            <span>{kindLabel}</span>
                          </span>
                        ) : null}
                      </div>
                      <span className={joinClassNames('chat-followup-inbox__urgency', `is-${urgency.tone}`)}>
                        {urgency.icon ? <i className={urgency.icon} aria-hidden="true"></i> : null}
                        <span>{urgency.label}</span>
                      </span>
                    </div>

                    <p className="chat-followup-inbox__message">
                      {anchorSenderLabel || ageLabel ? (
                        <span className="chat-followup-inbox__messageMeta">
                          {[anchorSenderLabel, ageLabel].filter(Boolean).join(' ・ ')}
                          {' ・ '}
                        </span>
                      ) : null}
                      {anchorExcerpt}
                    </p>

                    <div className="chat-followup-inbox__footer">
                      <div className="chat-followup-inbox__assignee">
                        {waitingOnArtist ? (
                          <span className="chat-followup-inbox__waiting">
                            <i className="fa-regular fa-hourglass-half" aria-hidden="true"></i>
                            <span>相手の返答待ち</span>
                          </span>
                        ) : assigneeName ? (
                          <>
                            <Avatar
                              imageUrl={assigneeAvatarUrl}
                              fallback={assigneeName.slice(0, 1)}
                              className="is-admin is-mini"
                            />
                            <span className="chat-followup-inbox__assigneeName">
                              {assigneeName}
                              {assigneeBasisLabel ? `（${assigneeBasisLabel}）` : ''}
                            </span>
                          </>
                        ) : (
                          <span className="chat-followup-inbox__assigneeName is-muted">担当 未割り当て</span>
                        )}
                      </div>

                      <div className="chat-followup-inbox__actions">
                        <button
                          type="button"
                          className="chat-followup-inbox__promoteButton"
                          disabled={isBusy || isExiting}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handlePromote(item);
                          }}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          <span>クイックタスク</span>
                        </button>
                        <button
                          type="button"
                          className="chat-followup-inbox__completeButton"
                          disabled={isBusy || isExiting}
                          aria-label="対応済みにする"
                          title="対応済みにする"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleComplete(item);
                          }}
                        >
                          <i className="fa-solid fa-check" aria-hidden="true"></i>
                          <span>完了</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChatFollowupInbox;
